/**
 * Compact bottom bar for guided mode phases.
 * Shows a stop button while AI is processing,
 * and a single-line feedback input when idle.
 */

import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Square, Send } from 'lucide-react';

interface GuidedInterventionBarProps {
  isProcessing: boolean;
  onStop: () => void;
  onSendFeedback: (message: string) => void;
}

export function GuidedInterventionBar({
  isProcessing,
  onStop,
  onSendFeedback,
}: GuidedInterventionBarProps) {
  const [feedback, setFeedback] = useState('');
  const [wasStopped, setWasStopped] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset wasStopped when processing starts again
  useEffect(() => {
    if (isProcessing) {
      setWasStopped(false);
    }
  }, [isProcessing]);

  // Auto-focus input after stop
  useEffect(() => {
    if (wasStopped && !isProcessing) {
      inputRef.current?.focus();
    }
  }, [wasStopped, isProcessing]);

  const handleStop = () => {
    setWasStopped(true);
    onStop();
  };

  const handleSend = () => {
    const trimmed = feedback.trim();
    if (!trimmed) return;
    onSendFeedback(trimmed);
    setFeedback('');
    setWasStopped(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const showFeedbackInput = wasStopped && !isProcessing;

  return (
    <div className="border-t px-4 py-2">
      {isProcessing ? (
        <div className="flex items-center">
          <Button onClick={handleStop} variant="destructive" size="sm" className="gap-2">
            <Square className="h-3.5 w-3.5" />
            Stop
          </Button>
        </div>
      ) : showFeedbackInput ? (
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type feedback to adjust..."
            className="h-9"
          />
          <Button
            onClick={handleSend}
            disabled={!feedback.trim()}
            size="icon"
            className="h-9 w-9 flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
